import i18n from "@/i18n/i18n";
import { featureFlags } from "@/config/env";
import http from "@/services/http";
import { mockManager, BrandComparisonData } from "@/services/mock";
import { getSourceId } from "@/utils/constants";

// 品牌对比图表数据接口
export interface BrandComparisonChartData {
  xLabels: string[]; // 时间序列标签
  series: Array<{
    name: string;
    values: number[];
    color?: string;
    isCurrentBrand?: boolean;
  }>;
  // 新增：品牌列表，用于品牌按钮刷新
  brands: Array<{
    brandId: string;
    brandName: string;
    color: string;
    isCurrentBrand: boolean;
  }>;
}

// 指标类型
export type MetricType = string;

// 指标显示名称映射
export const METRIC_LABELS: Record<MetricType, string> = {
  mentionRate: i18n.t("api.metric_mention_rate"),
  firstMentionRate: i18n.t("api.metric_first_mention"),
  firstRecommendationRate: i18n.t("api.metric_first_recommend"),
};

// 品牌颜色映射
// const BRAND_COLORS = [
//   '#6b5bd2', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
//   '#06b6d4', '#f97316', '#ec4899', '#84cc16', '#a855f7'
// ]

/**
 * 品牌对比 API
 */
export const brandComparisonApi = {
  /**
   * 获取品牌对比数据
   */
  async getComparisonData(
    brandId: string,
    categoryId: string,
    days: number = 7,
    metricType: string = "mentionRate,firstMentionRate,firstRecommendationRate",
    modelId: string = "all",
    startDay?: string,
    endDay?: string,
    qId?: string,
    sourceId?: number
  ): Promise<BrandComparisonData> {
    try {
      if (featureFlags.enableMock) {
        // 使用 brandId 构造 mock 所需上下文（名称找不到时用 brandId 兜底）
        return await mockManager.getBrandComparisonData(
          brandId,
          categoryId,
          days,
          modelId
        );
      }

      // 自动添加sourceId，如果没有指定则使用默认值
      const finalSourceId = sourceId ?? getSourceId();

      // 真实 API 调用
      const query: Record<string, string> = {
        brandId,
        categoryId,
        days: String(days),
        metricType,
        modelId,
        sourceId: finalSourceId.toString(),
      };

      // 添加可选参数
      if (startDay) {
        query.startDay = startDay;
      }
      if (endDay) {
        query.endDay = endDay;
      }
      if (qId) {
        query.qId = qId;
      }

      const urlParams = new URLSearchParams(query);

      const response = await http.get<any>(
        `data/brand/metrics/trend?${urlParams.toString()}`
      );

      if (!response || !response.data) {
        throw new Error("No comparison data received");
      }

      // 兼容后端外层 data 包裹
      const payload = response?.data?.data ?? response?.data;
      console.log("[brandComparison] raw payload:", response.data);
      const curBrand = payload?.brandList?.curBrand ?? {
        brandId,
        brandName: brandId,
      };
      const otherBrands =
        payload?.brandList?.otherBrand ?? payload?.brandList?.otherBrands ?? [];
      const rate = payload?.rateList ?? {};

      return {
        brandList: {
          curBrand,
          otherBrands,
        },
        rateList: {
          mentionRate: rate.mentionRate ?? {},
          firstMentionRate: rate.firstMentionRate ?? {},
          firstRecommendationRate: rate.firstRecommendationRate ?? {},
          positiveMentionRate: rate.positiveMentionRate ?? {},
          negativeMentionRate: rate.negativeMentionRate ?? {},
        },
      };
    } catch (error) {
      console.error("Brand comparison API error:", error);
      // 发生错误时返回空数据
      return {
        brandList: {
          curBrand: { brandId, brandName: brandId },
          otherBrands: [],
        },
        rateList: {
          mentionRate: {},
          firstMentionRate: {},
          firstRecommendationRate: {},
          positiveMentionRate: {},
          negativeMentionRate: {},
        },
      };
    }
  },
};

export default brandComparisonApi;
